import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { HOME_MEDIA, getApiBaseUrl, mediaUrl } from "../config/api";
import { getAdminSession, loginAdminRequest, saveAdminSession } from "../utils/adminSession";

function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [siteName, setSiteName] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/contacts`, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (cancelled) return;
        const current = Array.isArray(list) && list[0] ? list[0] : null;
        setCompanyName(String(current?.companyName || "").trim());
        setSiteName(String(current?.siteName || "").trim());
      })
      .catch(() => {
        if (cancelled) return;
        setCompanyName("");
        setSiteName("");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const data = await loginAdminRequest({ email, password, rememberMe });
      saveAdminSession({
        token: data.token,
        email: data.admin?.email,
        name: data.admin?.name,
        rememberMe: data.rememberMe,
        exp: data.exp,
      });
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(err?.message || "Unable to sign in");
    } finally {
      setBusy(false);
    }
  }

  if (getAdminSession()) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className="min-h-screen bg-at-dark lg:grid lg:grid-cols-2">
      <section className="relative h-[38vh] min-h-[240px] overflow-hidden text-white sm:h-[42vh] lg:h-auto lg:min-h-screen">
        <img
          src={mediaUrl(HOME_MEDIA.login)}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 flex h-full flex-col px-6 py-8 lg:px-12 lg:py-12">
          <div className="flex flex-col items-center pt-2 lg:pt-4">
            <img
              src={mediaUrl(HOME_MEDIA.logo)}
              alt=""
              className="h-16 w-16 object-contain invert sm:h-20 sm:w-20 lg:h-28 lg:w-28"
            />
            {companyName ? (
              <p className="mt-3 text-center text-[11px] font-semibold uppercase leading-tight tracking-[0.18em] sm:text-sm lg:whitespace-nowrap lg:text-base lg:tracking-[0.2em]">
                {companyName}
              </p>
            ) : null}
          </div>
          <div className="mt-auto hidden max-w-xl pb-8 lg:block">
            <h1 className="text-4xl font-semibold uppercase leading-tight md:text-[2.5rem]">
              <span className="block whitespace-nowrap tracking-[2px]">Manage your website.</span>
              <span className="mt-1 block whitespace-nowrap tracking-[2px] text-at-gold">All in one place.</span>
            </h1>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-white/75 md:text-base">
              Products, content, enquiries and insights—securely managed from one dashboard.
            </p>
          </div>
        </div>
      </section>

      <section className="relative -mt-5 flex flex-col rounded-t-[1.75rem] bg-at-paper px-6 pb-8 pt-8 sm:px-10 lg:mt-0 lg:rounded-none lg:px-16 lg:py-10">
        <form onSubmit={onSubmit} className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center">
          <p className="text-center text-[11px] font-semibold uppercase tracking-[0.22em] text-neutral-400 lg:text-left">
            Admin Portal
          </p>
          <h2 className="mt-2 text-center text-4xl font-semibold text-at-dark lg:text-left">Welcome back</h2>
          <p className="mt-2 text-center text-sm text-neutral-500 lg:text-left">
            {siteName ? `Sign in to manage the ${siteName} website.` : "Sign in to manage the website."}
          </p>

          <label className="mt-8 block text-sm font-medium text-at-dark" htmlFor="email">
            Email address
          </label>
          <div className="mt-2 flex items-center gap-2 rounded-md border border-neutral-200 bg-white px-3 py-2.5 focus-within:border-at-gold">
            <span className="text-neutral-400" aria-hidden>
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M4 6h16v12H4z" />
                <path d="M4 7l8 6 8-6" />
              </svg>
            </span>
            <input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@atindustrialsupply.com"
              className="w-full bg-transparent text-sm outline-none placeholder:text-neutral-300"
            />
          </div>

          <label className="mt-5 block text-sm font-medium text-at-dark" htmlFor="password">
            Password
          </label>
          <div className="mt-2 flex items-center gap-2 rounded-md border border-neutral-200 bg-white px-3 py-2.5 focus-within:border-at-gold">
            <span className="text-neutral-400" aria-hidden>
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
                <rect x="5" y="11" width="14" height="9" rx="1.5" />
                <path d="M8 11V8a4 4 0 018 0v3" />
              </svg>
            </span>
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent text-sm outline-none"
            />
            <button
              type="button"
              aria-label={showPassword ? "Hide password" : "Show password"}
              onClick={() => setShowPassword((v) => !v)}
              className="text-neutral-400 hover:text-at-dark"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
                {showPassword ? (
                  <>
                    <path d="M3 3l18 18" />
                    <path d="M10.5 10.7A3 3 0 0012 15a3 3 0 002.5-4.3" />
                    <path d="M17.9 17.4A11 11 0 0112 19C6 19 3 12 3 12a18 18 0 014.2-4.6" />
                  </>
                ) : (
                  <>
                    <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" />
                    <circle cx="12" cy="12" r="3" />
                  </>
                )}
              </svg>
            </button>
          </div>

          <div className="mt-4 flex items-center justify-between text-sm">
            <label className="flex items-center gap-2 text-neutral-600">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="rounded border-neutral-300"
              />
              Remember me
            </label>
            <span className="text-at-gold">Forgot password?</span>
          </div>

          {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

          <button
            type="submit"
            disabled={busy}
            className="mt-6 w-full rounded-md bg-at-gold py-3 text-sm font-semibold text-at-dark hover:bg-at-gold-hover disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Sign In →"}
          </button>

          <p className="mt-4 flex items-center justify-center gap-2 text-xs text-neutral-400">
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.8">
              <rect x="5" y="11" width="14" height="9" rx="1.5" />
              <path d="M8 11V8a4 4 0 018 0v3" />
            </svg>
            Authorised administrators only
          </p>
        </form>

        <div className="mx-auto mt-8 flex w-full max-w-md flex-col items-center gap-2 text-xs text-neutral-400 lg:flex-row lg:justify-between">
          <Link to="/" className="order-1 text-at-gold hover:text-at-gold-hover lg:order-2">
            Back to website →
          </Link>
          <span className="order-2 lg:order-1">© {new Date().getFullYear()}{companyName ? ` ${companyName}` : ""}</span>
        </div>
      </section>
    </div>
  );
}

export default LoginPage;
